import { useEffect, useState } from 'react';
import values from 'lodash/values';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { PROJECT_STATUS, PROJECT_ORDER_OPTIONS } from '../lib/constants';
import { cln } from '../lib/utils';
import { projectsPaginationAtom } from '../atoms';
import { useProjectsQuery } from '../lib/queries';
import { useDebounce } from '../hooks';
import { PATHS } from '../lib/paths';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { ProjectDetails } from '../components/ProjectDetails';

export default function ProjectsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pagination, setPagination] = useAtom(projectsPaginationAtom);
  const projectsQuery = useProjectsQuery(pagination);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 700);

  const statusFilter = pagination.filters.status?.$in;

  const setProjectsPaginationStatusFilter = (value: string[]) => {
    setPagination({
      ...pagination,
      filters: {
        ...pagination.filters,
        status: {
          $in: value,
        },
      },
    });
  };

  const handleFilterClick = (filter: string) => () => {
    const relevantStatus = statusFilter?.some((status) => status === filter);

    if (relevantStatus)
      setProjectsPaginationStatusFilter(statusFilter?.filter((f) => f !== filter) || []);
    else setProjectsPaginationStatusFilter([...(statusFilter || []), filter]);
  };

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        search: debouncedSearch,
      },
    }));
  }, [debouncedSearch, setPagination]);

  const orderDefaultValue = PROJECT_ORDER_OPTIONS.findIndex(
    (option) => option.direction === pagination.sort?.createdAt,
  );

  return (
    <main className="grid gap-2 w-700 mx-auto">
      <div className="flex items-center">
        <h1 className="font-bold text-hero grow">{t('projects.title')}</h1>
        <button className="primary-fill !pl-2" onClick={() => navigate(PATHS.newProjectPath())}>
          <i className="ri-add-line" />
          {t('project.title')}
        </button>
      </div>
      <div className="flex gap-4 items-end w-full mt-10 mb-5">
        <Input
          label="common.search"
          placeholder="search"
          className="grow"
          type="search"
          value={search}
          onInput={(e: any) => {
            setSearch(e.target.value);
          }}
        />
      </div>
      <div className="flex mb-5 gap-4">
        <div className="flex items-end gap-2">
          {values(PROJECT_STATUS).map((status) => (
            <button
              key={status}
              className={cln(
                'chip',
                statusFilter?.includes(status) && 'primary-fill border-transparent',
              )}
              onClick={handleFilterClick(status)}
            >
              {t(`project.status.${status}`)}
            </button>
          ))}
        </div>
        <Select
          label="order.label"
          className="justify-end"
          defaultValue={orderDefaultValue}
          onChange={(e: any) => {
            const { direction, field } = PROJECT_ORDER_OPTIONS[e.target.value];
            setPagination((p) => ({ ...p, sort: { ...p.sort, [field]: direction } }));
          }}
        >
          {PROJECT_ORDER_OPTIONS.map((opt, index) => (
            <option key={opt.label} value={index}>
              {t(opt.label)}
            </option>
          ))}
        </Select>
      </div>
      {projectsQuery.data?.projects.map((project) => (
        <ProjectDetails key={project.id} project={project} />
      ))}
    </main>
  );
}
